import type { TFunction } from 'i18next';
import {
  PARAMETER_OPTIONS,
  type MeasurementSessionDefaultsResponse,
  type ProcedureStep,
  type TestKitProfileResponse,
} from '../../../types/measurement';
import type { TestInterval } from '../../../services/testIntervalService';

/** One test of a guided session: the parameter, its kit and the kit's steps. */
export interface GuidedTest {
  parameterId: string;
  profile: TestKitProfileResponse | null;
  /** The kits the user can pick for this parameter (the kit picker on the test card). */
  kits: TestKitProfileResponse[];
  /** The kit's steps, or a single "read the value" when no procedure is known. */
  steps: ProcedureStep[];
  /** The procedure was checked against the manufacturer's instructions. */
  verified: boolean;
  /** The user's own timer lengths, by step index. */
  waitOverrides: Record<string, number>;
  lastValue: number | null;
  due: 'overdue' | 'due' | null;
}

const KNOWN = new Set(PARAMETER_OPTIONS.map((p) => p.id));
const READ_ONLY: ProcedureStep[] = [{ type: 'read_value' }];
/** Due within the next 12 h counts as due today. */
const DUE_SOON_MS = 12 * 3_600_000;

/**
 * The tests of a guided session, most urgent first (docs/voiceFeature.md,
 * UC-15/16): overdue tests, then due ones by their due date (test intervals,
 * #1823), then the tank's other usual parameters (session defaults). Every
 * parameter the session form knows can be guided; a kit without known steps
 * just asks for the value. `kitChoice` is the kit picked in this session
 * (parameter → profile id, null = no kit); it wins over the tank's saved kit.
 */
export function buildGuidedPlan(
  defaults: MeasurementSessionDefaultsResponse,
  profiles: readonly TestKitProfileResponse[],
  intervals: readonly TestInterval[],
  now: number = Date.now(),
  kitChoice: Readonly<Record<string, string | null>> = {},
): GuidedTest[] {
  const profileById = new Map(profiles.map((p) => [p.id, p]));
  const intervalByParameter = new Map(intervals.map((i) => [i.parameterId, i]));
  const ids: string[] = [];
  for (const i of intervals) if (i.mode !== 'off' && KNOWN.has(i.parameterId) && !ids.includes(i.parameterId)) ids.push(i.parameterId);
  for (const p of defaults.parameters) if (KNOWN.has(p.parameterId) && !ids.includes(p.parameterId)) ids.push(p.parameterId);

  const tests = ids.map((parameterId, order) => {
    const d = defaults.parameters.find((p) => p.parameterId === parameterId);
    const savedKitId = d?.testKitProfileId ?? null;
    const kitId = parameterId in kitChoice ? (kitChoice[parameterId] ?? null) : savedKitId;
    const found = kitId ? profileById.get(kitId) : undefined;
    const profile = found && found.parameterId === parameterId ? found : null;
    const interval = intervalByParameter.get(parameterId);
    const dueAt = interval?.dueAt ? new Date(interval.dueAt).getTime() : null;
    const due: GuidedTest['due'] = interval?.overdue ? 'overdue' : dueAt !== null && dueAt <= now + DUE_SOON_MS ? 'due' : null;
    const test: GuidedTest = {
      parameterId,
      profile,
      kits: profiles.filter((p) => p.parameterId === parameterId),
      steps: profile?.procedure?.steps?.length ? profile.procedure.steps : READ_ONLY,
      verified: profile?.procedure?.verified === true,
      // Own timer lengths belong to the saved kit; another kit starts from its own times.
      waitOverrides: profile && profile.id === savedKitId ? (d?.waitOverrides ?? {}) : {},
      lastValue: d?.lastValue ?? null,
      due,
    };
    return { test, order, dueAt };
  });

  const rank = (x: (typeof tests)[number]): number => (x.test.due === 'overdue' ? 0 : x.test.due === 'due' ? 1 : 2);
  return tests
    .sort((a, b) => rank(a) - rank(b) || (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity) || a.order - b.order)
    .map((x) => x.test);
}

/** Timer length of a step: the user's own, else the kit's; undefined = no timer. */
export function stepSeconds(test: GuidedTest, index: number): number | undefined {
  const step = test.steps[index];
  if (!step) return undefined;
  const own = test.waitOverrides[String(index)];
  if (typeof own === 'number' && (step.type === 'wait' || step.type === 'swirl')) return own;
  return step.type === 'wait' || step.type === 'swirl' ? step.seconds : undefined;
}

/** The translated instruction of a step ("Add 4 drops of NO3-1"). */
export function stepText(t: TFunction, step: ProcedureStep, seconds?: number): string {
  const count = step.amount ?? 1;
  switch (step.type) {
    case 'fill_sample':
      return step.ml ? t('guided.step.fill_sample', { ml: step.ml }) : t('guided.step.fill_sample_plain');
    case 'add_reagent':
      return t(`guided.step.add_reagent_${step.unit ?? 'drops'}`, { count, reagent: step.reagent ?? '' });
    case 'swirl':
      return seconds ? t('guided.step.swirl_for', { seconds }) : t('guided.step.swirl');
    case 'wait':
      return t('guided.step.wait', { minutes: Math.round(((seconds ?? step.seconds ?? 0) / 60) * 10) / 10 });
    case 'titrate':
      return t('guided.step.titrate', { reagent: step.reagent ?? '' });
    default:
      return t(`guided.step.${step.type}`);
  }
}
